/**
 * MagmaCursor — Global molten-gold cursor.
 *
 * Behaviour:
 *  - Follows the mouse via requestAnimationFrame (smooth lerp).
 *  - Grows and shifts hue on hover over interactive elements (a, button,
 *    [role=button], [data-cursor-hover]).
 *  - Preserves native caret on text inputs / textareas / contenteditable.
 *  - Disabled on touch devices (no pointer, no hover) and when
 *    prefers-reduced-motion is enabled.
 *
 * Mount once at App root. Requires `.magma-cursor` styles in index.css.
 */
import { useEffect, useRef } from "react";

const INTERACTIVE_SELECTOR = 'a, button, [role="button"], [data-cursor-hover], label[for], input[type="submit"], input[type="button"], summary, .cursor-pointer';
const TEXT_SELECTOR = 'input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea, [contenteditable="true"]';

export default function MagmaCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    // Skip on touch / no-pointer / reduced-motion
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isCoarse || reduced) return undefined;

    // Add the global class to enable cursor:none rules
    document.documentElement.classList.add("has-magma-cursor");

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return undefined;

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let ringX = targetX;
    let ringY = targetY;
    let raf = 0;

    const tick = () => {
      // Ring smoothly follows the dot with easing
      ringX += (targetX - ringX) * 0.18;
      ringY += (targetY - ringY) * 0.18;
      dot.style.transform = `translate3d(${targetX}px, ${targetY}px, 0) translate(-50%, -50%)`;
      ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      dot.style.opacity = "1";
      ring.style.opacity = "1";
    };

    const onLeave = () => {
      dot.style.opacity = "0";
      ring.style.opacity = "0";
    };
    const onEnter = () => {
      dot.style.opacity = "1";
      ring.style.opacity = "1";
    };

    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(TEXT_SELECTOR)) {
        // Hide magma cursor over text fields so users see the caret
        dot.style.opacity = "0";
        ring.style.opacity = "0";
        return;
      }
      dot.style.opacity = "1";
      ring.style.opacity = "1";
      if (t.closest(INTERACTIVE_SELECTOR)) {
        ring.classList.add("is-hover");
        dot.classList.add("is-hover");
      } else {
        ring.classList.remove("is-hover");
        dot.classList.remove("is-hover");
      }
    };

    const onDown = () => {
      dot.classList.add("is-click");
      ring.classList.add("is-click");
    };
    const onUp = () => {
      dot.classList.remove("is-click");
      ring.classList.remove("is-click");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);

    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.documentElement.classList.remove("has-magma-cursor");
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="magma-cursor-ring" aria-hidden="true" style={{ opacity: 0 }} />
      <div ref={dotRef} className="magma-cursor-dot" aria-hidden="true" style={{ opacity: 0 }} />
    </>
  );
}
